// ── Does this account have a password, and what do we ask for? ──────────────
//
// The Update Security card asked every account for its CURRENT password,
// including accounts that have never had one. Someone who signed up with Google
// was shown a required field they could not fill.
//
// ── THE SOURCE OF TRUTH IS `has_password`, NOT THE SIGN-IN METHOD ───────────
// The card used to decide with `oauth_provider && !has_password`, and the first
// half of that is what broke it. `users.oauth_provider` is only written by the
// PLUGIN's OAuth path (createOAuthUser in auth.routes.ts). A web Google sign-in
// goes through Supabase — signInWithOAuth, then the row is created for us — and
// nothing ever tells our API it was Google, so the column stays NULL. The test
// therefore read false for exactly the users it existed to catch, and they got
// the Current Password field anyway.
//
// OLD: `has_password` has no such gap: the API computes it as `!!user.password_hash`
// OLD: on /users/by-email, straight from the column the login route itself checks
// OLD: (`user.oauth_provider && !user.password_hash`). It is the same fact the server
// OLD: acts on, so the form and the backend cannot disagree.
// (Wrong for every WEB email signup: supabase.auth.signUp() never writes
// password_hash, so those accounts were told they had no password.)
//
// `has_password` comes from /users/by-email and is true when the account has a
// password in EITHER place: our users.password_hash, or Supabase Auth
// (auth.users.encrypted_password), which is where a web email signup's password
// actually lives. See hasPasswordCredential in the API's user.models.ts. It is
// null when that lookup fails, which the check below treats as "has one".
//
// And it is the RIGHT question. Whether to demand a current password depends on
// whether one exists — not on how the account was created. A Google user who
// has since set a password must be asked for it; an email user who somehow has
// none must not be. Keying on the password itself makes both fall out for free,
// and covers a third case nobody has to enumerate: a future provider.
//
// The sign-in provider is still used, but only for WORDING — "you signed up
// with Google" is friendlier than "this account has no password". It is read
// from Supabase's own identity rather than our column, because that is the
// system that actually performed the sign-in and is right even when
// oauth_provider is null.

/** Title-case a provider id for display: 'google' → 'Google'. */
const prettyProvider = (id) => {
  const s = String(id || '').trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/**
 * Which social provider signed this session in, or null for email/password.
 *
 * Read from the SUPABASE user, which is the authority on how the session was
 * established: `app_metadata.provider` is the one used, and `identities` lists
 * every one linked. Our own `oauth_provider` column is accepted as a fallback
 * for accounts created through the plugin's OAuth path, which does write it.
 *
 * 'email' is not a social provider and returns null — it is the ordinary case
 * and needs no explanation on screen.
 */
export function signInProvider(supabaseUser, userData) {
  const appMeta = supabaseUser?.app_metadata || {};
  const userMeta = supabaseUser?.user_metadata || {};
  const notEmail = (p) => p && p !== 'email';

  // 1. app_metadata.providers — EVERY provider linked to the account, and the
  //    only signal that survives the case this function kept getting wrong: a
  //    Google account whose last sign-in was by email reports
  //    app_metadata.provider === 'email', because that field is the provider
  //    USED, not the providers available. AuthContext already relies on this
  //    array for the same reason, and says so.
  const fromProviders = Array.isArray(appMeta.providers)
    ? appMeta.providers.find(notEmail)
    : null;

  // 2. identities — the same fact, but usually ABSENT here: it is returned by
  //    getUser(), while the user we are handed comes from the session, decoded
  //    from the JWT. Kept because it is authoritative when present.
  const fromIdentities = Array.isArray(supabaseUser?.identities)
    ? supabaseUser.identities.map((i) => i?.provider).find(notEmail)
    : null;

  // 3. The provider used for THIS session. Right whenever the current sign-in
  //    was social; silent (reports 'email') when it was not.
  const fromCurrent = notEmail(appMeta.provider) ? appMeta.provider : null;

  // 4. Two Google-specific traces left in user_metadata by the OAuth exchange.
  //    They only appear on an account that has been through Google, so they
  //    cannot produce a false positive for an email-only account — and Google
  //    is the only social provider this product offers, so naming it is safe.
  const fromGoogleTrace = (typeof userMeta.iss === 'string' && userMeta.iss.includes('google'))
    || userMeta.provider_id
    ? 'google'
    : null;

  // 5. Our own column. Written only by the plugin's OAuth path, never by a web
  //    sign-in, so it is the last resort rather than the first.
  const raw = fromProviders
    || fromIdentities
    || fromCurrent
    || fromGoogleTrace
    || userData?.oauth_provider
    || null;

  return notEmail(raw) ? String(raw) : null;
}

/**
 * What the Update Security card should show and do.
 *
 * @param hasPassword  userData.has_password — `!!users.password_hash` from the API
 * @param provider     signInProvider(), or null for an email/password account
 *
 * @returns {{
 *   requiresCurrentPassword: boolean,  show and demand the Current Password field
 *   isFirstTimeSet: boolean,           this creates a password rather than changing one
 *   title: string,                     the card heading
 *   buttonLabel: string,               and its action
 *   notice: string|null,               the explanation, when one is owed
 * }}
 */
export function passwordSecurityState({ hasPassword, provider } = {}) {
  // Anything other than an explicit false means "has one". An undefined
  // has_password — an older API build, a payload that has not arrived — must
  // fail SAFE: asking for a current password that turns out not to exist is a
  // recoverable annoyance, while skipping the check on an account that does
  // have one would let a borrowed session change the password unchallenged.
  const noPassword = hasPassword === false;

  const name = prettyProvider(provider);

  if (!noPassword) {
    return {
      requiresCurrentPassword: true,
      isFirstTimeSet: false,
      title: 'Update Security',
      buttonLabel: 'Update password',
      // An account that has BOTH ways in is told so, and told that changing
      // one does not disturb the other. Without that, changing a password on
      // an account you normally reach through Google looks like it might cost
      // you the Google route — so the notice exists to remove a hesitation,
      // not to describe the form.
      //
      // Silent for an ordinary email account: there is no second way in, so
      // there is nothing to reassure anyone about, and a notice would be noise
      // on the screen every customer sees.
      notice: name
        ? `You can sign in with ${name} or with your email and password. `
          + `Changing your password here won't affect ${name} sign-in.`
        : null,
    };
  }

  return {
    requiresCurrentPassword: false,
    isFirstTimeSet: true,
    title: 'Set a Password',
    buttonLabel: 'Set password',
    // Wording as specified. It answers the three questions someone has at
    // this moment, in the order they arrive: why is there no current password,
    // what does setting one get me, and do I lose the way I sign in today.
    // That last sentence is the one people need most — without it, adding a
    // password reads as replacing Google rather than adding to it.
    notice: name
      ? `You signed up with ${name}, so you don't have a password yet. `
        + 'Set one to also sign in with your email address and password. '
        + `${name} sign-in will keep working.`
      // No provider to name — the same three answers, minus the one we cannot
      // give honestly.
      : "You don't have a password yet. Set one to also sign in with your "
        + 'email address and password.',
  };
}

/** The shortest password we accept. Matches the server's own rule. */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * Check the form before anything is sent.
 *
 * Returns an error message, or null when it is fine. Ordered the way someone
 * fills the form in, so the first thing they are told is the first thing they
 * can act on.
 */
export function validatePasswordForm({
  currentPassword, newPassword, confirmPassword, requiresCurrentPassword,
}) {
  if (requiresCurrentPassword && !String(currentPassword || '')) {
    return 'Enter your current password.';
  }
  if (String(newPassword || '').length < MIN_PASSWORD_LENGTH) {
    return `Your new password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (newPassword !== confirmPassword) {
    return 'The new passwords do not match.';
  }
  // Changing a password to itself is not a change, and the provider will accept
  // it silently — leaving someone certain they have rotated a password they
  // have not.
  if (requiresCurrentPassword && currentPassword === newPassword) {
    return 'Your new password must be different from your current one.';
  }
  return null;
}
